import CrownIcon from "@/assets/icons/crown.svg?react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { useCurrentUserProfile } from "@/hooks/current-user-profile.hook";
import { useUsersByCompany } from "@/hooks/users-by-company.hook";
import { mapRoleToLabel } from "@/mappers/users";
import { GetUsersByCompanyIdResponseContent } from "@/requests/users/users-by-company.request";
import { MoreCircle } from "iconsax-react";
import { useState } from "react";
import NewUserForm from "./new-user-form";
import RemoveUserDialog from "./remove-user-dialog";

type User = GetUsersByCompanyIdResponseContent;

const UsersCard = () => {
  const [removeDialogIsOpen, setRemoveDialogIsOpen] = useState<boolean>(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);

  const { data: userProfile } = useCurrentUserProfile();

  const { data: users, isLoading } = useUsersByCompany(
    userProfile?.companyId || "",
  );

  const handleDeleteAction = (user: User) => {
    setUserToDelete(user);
    setRemoveDialogIsOpen(!!user);
  };

  return (
    <Card>
      <CardContent className="px-3 py-6">
        <div className="mb-4 flex items-center gap-4">
          <h3 className="grow text-xl font-semibold text-[#13171B]">
            Usuarios
          </h3>

          <NewUserForm />
        </div>

        {isLoading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} style={{ width: "100%", height: "28px" }} />
            ))}
          </div>
        ) : (
          <table className="w-full">
            <thead className="rounded">
              <tr className="rounded-lg text-sm">
                <th className="rounded-l-full bg-[#F0F2F4] px-3 py-0.5 text-left">
                  Nombre
                </th>
                <th className="bg-[#F0F2F4] px-3 py-0.5 text-left">Rol</th>
                <th className="bg-[#F0F2F4] px-3 py-0.5 text-left">
                  Correo electrónico
                </th>
                <th className="rounded-r-full bg-[#F0F2F4]"></th>
              </tr>
            </thead>
            <tbody>
              {users?.content.map((user) => (
                <tr key={user.id}>
                  <td className="px-3 py-2 text-sm">{user.name}</td>
                  <td className="px-3 py-2 text-sm">
                    {mapRoleToLabel(user.role)}
                    {user.role === "ORG_ADMIN" && (
                      <CrownIcon className="ml-1 inline-block" />
                    )}
                  </td>
                  <td className="px-3 py-2 text-sm">{user.email}</td>
                  <td className="text-right">
                    {user.id !== userProfile?.id && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreCircle className="text-[#4F5D6D]" size={16} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onSelect={() => handleDeleteAction(user)}
                            className="text-red-600"
                          >
                            Eliminar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <RemoveUserDialog
          isOpen={removeDialogIsOpen}
          onOpenChange={setRemoveDialogIsOpen}
          user={userToDelete}
        />
      </CardContent>
    </Card>
  );
};

export default UsersCard;
